import React, { useEffect } from "react";
import { Router, Route, Switch, Redirect, useLocation } from "react-router-dom";
import { QueryParamProvider } from "use-query-params";
import { createBrowserHistory } from "history";
import { useIntercom } from "react-use-intercom";
import ahoy from "ahoy.js";

const history = createBrowserHistory();

import PrivateRoute from "./PrivateRoute";
import ReviewsRouter from "./ReviewsRouter";
import useViewer from "../hooks/useViewer";

import ViewerProfileScreen from "../screens/ViewerProfileScreen";
import LoginScreen from "../components/login";
import ErrorScreen from "../screens/ErrorScreen";

import useToggleIntercom from "../hooks/useToggleIntercom";

import TemplateListScreen from "../screens/TemplateListScreen";
import EditTemplateScreen from "../screens/EditTemplateScreen";
import NewTemplateScreen from "../screens/NewTemplateScreen";

import CampaignsRouter from "./CampaignsRouter";
import UsersRouter from "./UsersRouter";
import ActivityListScreen from "../screens/ActivityListScreen";
import CompanyConfigurationScreen from "../screens/CompanyConfigurationScreen";

import AnalyticsRouter from "./AnalyticsRouter";
import GroupListScreen from "../screens/GroupListScreen";
import ProfessionSkillLibraryScreen from "../screens/ProfessionSkillLibraryScreen";
import EditTranslationsScreen from "../screens/EditTranslationsScreen";
import ReportPage from "../components/reports";

const ScrollToTop = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
};

const IntercomManager = () => {
  const viewer = useViewer();
  const { update } = useIntercom();
  const { pathname } = useLocation();

  useEffect(() => {
    if (!viewer) {
      return;
    }

    update({
      name: viewer.name,
      avatar: {
        type: "avatar",
        imageUrl: viewer.avatar,
      },
      company: {
        companyId: viewer.company.id,
        name: viewer.company.name,
      },
      email: viewer.email,
      user_id: viewer.id,
      last_request_at: parseInt(new Date().getTime() / 1000),
    });
  }, [viewer, pathname]);

  useToggleIntercom(true);

  return null;
};

const AhoyManager = () => {
  const { pathname } = useLocation();

  useEffect(() => {
    ahoy.track("history", { pathname });
  }, [pathname]);

  return null;
}

function AppRouter() {
  const viewer = useViewer();

  return (
    <Router history={history}>
      <ScrollToTop />
      <IntercomManager />
      <AhoyManager />
      <QueryParamProvider ReactRouterRoute={Route}>
        <ScrollToTop />
        <Switch>
          <Route
            path="/login"
            exact
            render={(props) =>
              viewer ? (
                <Redirect to={{ pathname: "/" }} />
              ) : (
                <LoginScreen {...props} />
              )
            }
          />
          <Route path="/forgotten-password" exact component={LoginScreen} />
          <Route path="/connection-link" exact component={LoginScreen} />
          <Route path="/saml-sso" exact component={LoginScreen} />
          <Route path="/reset-password" exact component={LoginScreen} />
          <PrivateRoute path="/profile" component={ViewerProfileScreen} />

          <PrivateRoute
            path="/dashboard"
            exact
            component={ActivityListScreen}
          />
          <PrivateRoute path="/reviews" component={ReviewsRouter} />

          {/* remove me soon */}
          <PrivateRoute path="/mads" component={ReviewsRouter} />

          <PrivateRoute
            path={`/reports/:reportId`}
            withLayout={false}
            component={ReportPage}
          />
          <PrivateRoute path="/campaigns" component={CampaignsRouter} />

          <PrivateRoute path="/users" component={UsersRouter} />

          <PrivateRoute path="/analytics" component={AnalyticsRouter} />
          <PrivateRoute
            exact
            path={`/company`}
            component={CompanyConfigurationScreen}
          />
          <PrivateRoute
            exact
            path={`/company/translations`}
            component={EditTranslationsScreen}
          />
          <PrivateRoute
            exact
            path={`/templates/new`}
            component={NewTemplateScreen}
          />
          <PrivateRoute
            exact
            path={`/templates`}
            component={TemplateListScreen}
          />
          <PrivateRoute
            exact
            path={`/templates/:id/edit`}
            component={EditTemplateScreen}
            key="editTempalte"
          />
          <PrivateRoute
            exact
            path={`/templates/:id/copy`}
            component={EditTemplateScreen}
            key="copyTempalte"
          />
          <PrivateRoute
            exact
            path={`/groups`}
            component={GroupListScreen}
            key="groupList"
          />
          <PrivateRoute
            exact
            path={`/profession-skill-library`}
            component={ProfessionSkillLibraryScreen}
            key="professionSkillLibrary"
          />
          <PrivateRoute
            path="/"
            component={() => (
              <Redirect
                to={
                  viewer.isCapableOfReadingActivities
                    ? "/dashboard"
                    : "/reviews"
                }
              />
            )}
            exact
          />

          <Route
            path="*"
            render={() => <ErrorScreen type="PageNotFound"/>}
          />
        </Switch>
      </QueryParamProvider>
    </Router>
  );
}

export default AppRouter;
